import React from "react";
import { useLocation } from "react-router-dom";
import { useIOSNotification } from "../hooks/useIOSNotification";
import { ArrowUpOnSquareIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../features/auth/hooks/useAuth";
import { useActiveChat } from "../context/ActiveChatContext";
import { isMobileDevice } from "../services/mobileUtils";
import { useToast } from "../context/ToastContext";

const IOS_TOAST_DISMISSED_KEY = "ios_notification_toast_dismissed_until";
const THIRTY_MINUTES_IN_MS = 30 * 60 * 1000;

export const IOSInstallationBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { needsInstallation } = useIOSNotification();
  const { activeChat } = useActiveChat();
  const location = useLocation();
  const { showToast, hideToast } = useToast();
  const hasShownToastRef = React.useRef(false);

  const isAdminArea = location.pathname.startsWith("/admin");

  // Check if we're in mobile chat view
  const isInMobileChatView = React.useMemo(() => {
    return (
      isMobileDevice() && location.pathname === "/chats" && activeChat !== null
    );
  }, [location.pathname, activeChat]);

  const shouldShow =
    isAuthenticated &&
    needsInstallation &&
    !isInMobileChatView &&
    !isAdminArea;

  // Check if toast was recently dismissed
  const canShowToast = React.useMemo(() => {
    // Don't show if user is not authenticated
    if (!isAuthenticated) return false;
    
    if (!shouldShow) return false;
    
    const dismissedUntil = localStorage.getItem(IOS_TOAST_DISMISSED_KEY);
    if (dismissedUntil && Number(dismissedUntil) > Date.now()) {
      return false; // Still within dismissal period
    }
    
    // Clear expired dismissal
    if (dismissedUntil) {
      localStorage.removeItem(IOS_TOAST_DISMISSED_KEY);
    }
    
    return true;
  }, [isAuthenticated, shouldShow]);

  React.useEffect(() => {
    if (canShowToast && !hasShownToastRef.current) {
      hasShownToastRef.current = true;
      showToast(
        <div className="flex items-start gap-3 p-1">
          <div className="flex-shrink-0 mt-0.5">
            <ArrowUpOnSquareIcon className="h-6 w-6 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base text-gray-900 mb-1">
              Attiva le notifiche
            </h3>
            <p className="text-sm text-gray-700">
              Premi <ArrowUpOnSquareIcon className="inline h-5 w-5 mx-0.5 align-text-bottom" /> dalla barra inferiore
              e seleziona "Aggiungi a Home" per attivarle
            </p>
          </div>
          <button
            onClick={() => {
              const dismissUntil = Date.now() + THIRTY_MINUTES_IN_MS;
              localStorage.setItem(IOS_TOAST_DISMISSED_KEY, dismissUntil.toString());
              hideToast();
              hasShownToastRef.current = false;
            }}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Chiudi"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>,
        { duration: "infinite", interactive: true }
      );
    }
  }, [canShowToast, showToast, hideToast]);

  // Reset when conditions change
  React.useEffect(() => {
    if (!canShowToast) {
      hasShownToastRef.current = false;
    }
  }, [canShowToast]);

  return null;
};
